import type { DesignSystemContent } from 'designSystem/content/types';
import { DesignSystemSection } from 'designSystem/types';

export interface SearchResult {
	section: DesignSystemSection;
	contentPath?: string;
	label: string;
	matchContext?: string; // snippet showing where match occurred
	relevance: RelevanceTier;
}

//----------------------------------------------------------------------
// RELEVANCE TIERS
//----------------------------------------------------------------------
// Results are sorted by relevance tier (lower = more relevant), then by section order.
//
// Tier 1 - WORD_START: Query matches at start of any word in the name/title
//          Examples: "log" matches "Logo" or "Brand Logo" (starts word "Logo")
//
// Tier 2 - NAME_CONTAINS: Query appears within a word in the name/title
//          Examples: "log" matches "Backlog" (embedded in word)
//
// Tier 3 - PRIMARY_FIELD: Query matches in identity-like fields
//          Examples: tag names, CSS variable names, asset paths
//
// Tier 4 - SECONDARY_FIELD: Query matches in descriptive fields
//          Examples: descriptions, prop names/descriptions
//
// Tier 5 - DEEP_CONTENT: Query matches in implementation details
//          Examples: code snippets, deeply nested content

export enum RelevanceTier {
	WORD_START = 1,
	NAME_CONTAINS = 2,
	PRIMARY_FIELD = 3,
	SECONDARY_FIELD = 4,
	DEEP_CONTENT = 5,
}

// Checks if query matches at the start of any word in text
// "log" in "Logo" → true (starts the word)
// "log" in "Brand Logo" → true (starts the word "Logo")
// "log" in "Backlog" → false (embedded within word)
const matchesWordStart = (text: string, query: string): boolean => {
	const lowerText = text.toLowerCase();
	const lowerQuery = query.toLowerCase();

	// Check if text starts with query
	if (lowerText.startsWith(lowerQuery)) return true;

	// Check if query appears after a word boundary (space, hyphen, underscore, etc.)
	const wordBoundaryPattern = new RegExp(`[\\s\\-_/]${escapeRegex(lowerQuery)}`, 'i');
	return wordBoundaryPattern.test(text);
};

// Escape special regex characters in a string
const escapeRegex = (str: string): string => {
	return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Extracts a snippet of text around a match for context
const extractMatchContext = (
	text: string,
	query: string,
	maxLength: number = 80
): string | undefined => {
	const lowerText = text.toLowerCase();
	const lowerQuery = query.toLowerCase();
	const index = lowerText.indexOf(lowerQuery);

	if (index === -1) return undefined;

	const start = Math.max(0, index - 20);
	const end = Math.min(text.length, index + query.length + 20);
	let snippet = text.slice(start, end);

	if (start > 0) snippet = '...' + snippet;
	if (end < text.length) snippet = snippet + '...';

	return snippet.length > maxLength ? snippet.slice(0, maxLength) + '...' : snippet;
};

// Searches guidelines
const searchGuidelines = (
	guidelines: DesignSystemContent['guidelines'],
	query: string
): SearchResult[] => {
	if (!guidelines) return [];

	const results: SearchResult[] = [];
	const lowerQuery = query.toLowerCase();

	for (const guideline of guidelines) {
		const titleContains = guideline.title.toLowerCase().includes(lowerQuery);
		const titleWordStart = matchesWordStart(guideline.title, query);
		const contentMatch = guideline.content.toLowerCase().includes(lowerQuery);

		if (titleContains || contentMatch) {
			let relevance: RelevanceTier;
			if (titleWordStart) {
				relevance = RelevanceTier.WORD_START;
			} else if (titleContains) {
				relevance = RelevanceTier.NAME_CONTAINS;
			} else {
				relevance = RelevanceTier.DEEP_CONTENT;
			}

			results.push({
				section: 'guidelines',
				contentPath: guideline.path,
				label: guideline.title,
				matchContext: titleContains ? undefined : extractMatchContext(guideline.content, query),
				relevance,
			});
		}
	}

	return results;
};

// Searches gallery entries
const searchGallery = (gallery: DesignSystemContent['gallery'], query: string): SearchResult[] => {
	if (!gallery) return [];

	const results: SearchResult[] = [];
	const lowerQuery = query.toLowerCase();

	for (const entry of gallery) {
		const titleContains = entry.title.toLowerCase().includes(lowerQuery);
		const titleWordStart = matchesWordStart(entry.title, query);
		const codeMatch = entry.code.toLowerCase().includes(lowerQuery);

		if (titleContains || codeMatch) {
			let relevance: RelevanceTier;
			if (titleWordStart) {
				relevance = RelevanceTier.WORD_START;
			} else if (titleContains) {
				relevance = RelevanceTier.NAME_CONTAINS;
			} else {
				relevance = RelevanceTier.DEEP_CONTENT;
			}

			results.push({
				section: 'gallery',
				contentPath: entry.path,
				label: entry.title,
				matchContext: titleContains ? undefined : extractMatchContext(entry.code, query),
				relevance,
			});
		}
	}

	return results;
};

// Searches components
const searchComponents = (
	components: DesignSystemContent['components'],
	query: string
): SearchResult[] => {
	if (!components?.all) return [];

	const results: SearchResult[] = [];
	const lowerQuery = query.toLowerCase();

	for (const component of components.all) {
		const nameContains = component.name.toLowerCase().includes(lowerQuery);
		const nameWordStart = matchesWordStart(component.name, query);
		const tagContains = component.tagName.toLowerCase().includes(lowerQuery);
		const tagWordStart = matchesWordStart(component.tagName, query);
		const propsMatch = component.props.some(
			(prop) =>
				prop.name.toLowerCase().includes(lowerQuery) ||
				prop.description?.toLowerCase().includes(lowerQuery)
		);

		if (nameContains || tagContains || propsMatch) {
			let matchContext: string | undefined;
			let relevance: RelevanceTier;

			// Determine relevance tier based on what matched
			if (nameWordStart) {
				relevance = RelevanceTier.WORD_START;
			} else if (nameContains) {
				relevance = RelevanceTier.NAME_CONTAINS;
			} else if (tagWordStart) {
				relevance = RelevanceTier.PRIMARY_FIELD;
			} else if (tagContains) {
				relevance = RelevanceTier.PRIMARY_FIELD;
			} else {
				relevance = RelevanceTier.SECONDARY_FIELD;
				const matchingProp = component.props.find(
					(prop) =>
						prop.name.toLowerCase().includes(lowerQuery) ||
						prop.description?.toLowerCase().includes(lowerQuery)
				);
				if (matchingProp) {
					matchContext = matchingProp.description
						? `prop: ${matchingProp.name} - ${matchingProp.description}`
						: `prop: ${matchingProp.name}`;
				}
			}

			results.push({
				section: 'components',
				contentPath: component.id,
				label: component.name,
				matchContext,
				relevance,
			});
		}
	}

	return results;
};

// Searches theme tokens
const searchTheme = (theme: DesignSystemContent['theme'], query: string): SearchResult[] => {
	if (!theme?.all) return [];

	const results: SearchResult[] = [];
	const lowerQuery = query.toLowerCase();

	for (const token of theme.all) {
		const nameContains = token.name.toLowerCase().includes(lowerQuery);
		const nameWordStart = matchesWordStart(token.name, query);
		const cssVarContains = token.cssVariable.toLowerCase().includes(lowerQuery);
		const cssVarWordStart = matchesWordStart(token.cssVariable, query);
		const valueMatch = token.value.original.toLowerCase().includes(lowerQuery);

		if (nameContains || cssVarContains || valueMatch) {
			let relevance: RelevanceTier;
			if (nameWordStart) {
				relevance = RelevanceTier.WORD_START;
			} else if (nameContains) {
				relevance = RelevanceTier.NAME_CONTAINS;
			} else if (cssVarWordStart || cssVarContains) {
				relevance = RelevanceTier.PRIMARY_FIELD;
			} else {
				relevance = RelevanceTier.SECONDARY_FIELD;
			}

			results.push({
				section: 'theme',
				contentPath: undefined, // Theme view doesn't support selecting individual tokens yet
				label: token.name,
				matchContext: valueMatch && !nameContains ? token.value.original : undefined,
				relevance,
			});
		}
	}

	return results;
};

// Searches style tokens
const searchStyles = (styles: DesignSystemContent['styles'], query: string): SearchResult[] => {
	if (!styles?.tokens?.all) return [];

	const results: SearchResult[] = [];
	const lowerQuery = query.toLowerCase();

	for (const token of styles.tokens.all) {
		const nameContains = token.name.toLowerCase().includes(lowerQuery);
		const nameWordStart = matchesWordStart(token.name, query);
		const cssVarContains = token.cssVariable.toLowerCase().includes(lowerQuery);
		const cssVarWordStart = matchesWordStart(token.cssVariable, query);
		const valueMatch = token.value.original.toLowerCase().includes(lowerQuery);

		if (nameContains || cssVarContains || valueMatch) {
			let relevance: RelevanceTier;
			if (nameWordStart) {
				relevance = RelevanceTier.WORD_START;
			} else if (nameContains) {
				relevance = RelevanceTier.NAME_CONTAINS;
			} else if (cssVarWordStart || cssVarContains) {
				relevance = RelevanceTier.PRIMARY_FIELD;
			} else {
				relevance = RelevanceTier.SECONDARY_FIELD;
			}

			results.push({
				section: 'styles',
				contentPath: undefined, // Styles view doesn't support selecting individual tokens yet
				label: token.name,
				matchContext: valueMatch && !nameContains ? token.value.original : undefined,
				relevance,
			});
		}
	}

	return results;
};

// Searches assets
const searchAssets = (assets: DesignSystemContent['assets'], query: string): SearchResult[] => {
	if (!assets?.all) return [];

	const results: SearchResult[] = [];
	const lowerQuery = query.toLowerCase();

	for (const asset of assets.all) {
		const nameContains = asset.name.toLowerCase().includes(lowerQuery);
		const nameWordStart = matchesWordStart(asset.name, query);
		const pathContains = asset.path.toLowerCase().includes(lowerQuery);
		const pathWordStart = matchesWordStart(asset.path, query);
		const categoryContains = asset.category.toLowerCase().includes(lowerQuery);
		const categoryWordStart = matchesWordStart(asset.category, query);

		if (nameContains || pathContains || categoryContains) {
			let relevance: RelevanceTier;
			if (nameWordStart) {
				relevance = RelevanceTier.WORD_START;
			} else if (nameContains) {
				relevance = RelevanceTier.NAME_CONTAINS;
			} else if (pathWordStart || categoryWordStart) {
				relevance = RelevanceTier.PRIMARY_FIELD;
			} else if (pathContains || categoryContains) {
				relevance = RelevanceTier.PRIMARY_FIELD;
			} else {
				relevance = RelevanceTier.SECONDARY_FIELD;
			}

			results.push({
				section: 'assets',
				contentPath: asset.path,
				label: asset.name,
				matchContext: pathContains && !nameContains ? asset.path : undefined,
				relevance,
			});
		}
	}

	return results;
};

// Section order for secondary sorting (after relevance).
// Components are prioritized over gallery when relevance is equal.
const SECTION_ORDER: Record<DesignSystemSection, number> = {
	components: 0,
	guidelines: 1,
	theme: 2,
	styles: 3,
	assets: 4,
	foundations: 5,
	gallery: 6,
};

// Main search function
export const searchDesignSystem = (
	content: DesignSystemContent | null,
	query: string
): SearchResult[] => {
	if (!content || !query.trim()) return [];

	const trimmedQuery = query.trim();
	const results: SearchResult[] = [];

	results.push(...searchGuidelines(content.guidelines, trimmedQuery));
	results.push(...searchGallery(content.gallery, trimmedQuery));
	results.push(...searchComponents(content.components, trimmedQuery));
	results.push(...searchTheme(content.theme, trimmedQuery));
	results.push(...searchStyles(content.styles, trimmedQuery));
	results.push(...searchAssets(content.assets, trimmedQuery));

	// Sort by relevance tier first, then by section order
	results.sort((a, b) => {
		if (a.relevance !== b.relevance) {
			return a.relevance - b.relevance;
		}
		return SECTION_ORDER[a.section] - SECTION_ORDER[b.section];
	});

	// Limit results to avoid overwhelming UI
	return results.slice(0, 15);
};
